import cuid from 'cuid'
import { detect } from 'detect-browser'
import captureError from '@disco/utils/captureError'
import Storage from 'disco-storage'
import get from 'lodash/get'
// @ts-ignore p-queue is an ECMAScript module
import PQueue from 'p-queue'

import type { TempItem } from 'types/shortlist'
import type { Track } from 'types/track'

import api from '@disco/api/server'

import type { TempTrack } from 'types/shortlist'
import { getID3Tags } from '@disco/utils/getID3Tags'
import isAudioFile from '@disco/utils/isAudioFile'
import { retryPromise } from '@disco/utils/promise'

import { getExtension } from '@disco/utils/file-utils'
import { TRACK } from '@disco/constants/ItemTypes'

import type { UINotification } from 'types/notification'

import {
  MAX_UPLOAD_FILES_ERROR_LIMIT,
  MAX_UPLOAD_FILES_WARNING_LIMIT,
  MAX_UPLOAD_SIZE,
} from '@disco/constants/upload'

import { formatFileSize } from '@disco/utils/file-utils'

type Task = {
  // only set once p-queue starts the task; a queued task has none yet
  promise?: Promise<any>
  id: string
  abortController: AbortController
}

export type UploadTask = {
  func: (...params: any[]) => Promise<any>
  id: string
  params: any[]
}

/**
 * Removes File objects from temporary items, keeping only metadata.
 *
 * Useful for serialization or when File objects are no longer needed
 * but metadata should be preserved.
 *
 * @param tempItems - Array of temporary items containing files
 * @returns Array of items with File objects removed
 *
 * @example
 * ```typescript
 * const tempItems = [{ file: fileObj, name: 'song.mp3', size: 1024 }]
 * stripFiles(tempItems) // returns [{ name: 'song.mp3', size: 1024 }]
 * ```
 */
export function stripFiles(tempItems: TempItem[]): Omit<TempItem, 'file'>[] {
  return tempItems.map(item => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { file, ...rest } = item
    return rest
  })
}

class TaskRunner {
  _queue = new PQueue({ concurrency: 3 })
  _tasks: Task[] = []

  // removes task without cancelling it (can be used for finished tasks or tasks that thrown exception)
  _removeRunningTask: (id: string) => void = id => {
    const index = this._tasks.findIndex(task => task.id === id)
    if (index > -1) this._tasks.splice(index, 1)
  }

  // cancel a task without removing it. Also works before p-queue started it:
  // the queued job checks the signal and bails out instead of uploading a file
  // the user already cancelled.
  _cancelRunningTask: (id: string) => void = id => {
    const task = this._tasks.find(task => task.id === id)
    task?.abortController.abort()
  }

  // removes task from the queue and from running tasks
  removeTask: (id: string) => void = id => {
    this._cancelRunningTask(id)
    this._removeRunningTask(id)
  }

  addTask: (task: UploadTask) => Promise<any> = task => {
    const id = task.id
    const abortController = new AbortController()
    // Registered before queueing rather than when the job starts, so
    // removeTask()/cancelTasks() can reach a task p-queue has not picked up
    // yet — with concurrency 3, most tasks of a multi-file upload are waiting.
    const registeredTask: Task = { id, abortController }
    this._tasks.push(registeredTask)

    const job = () => {
      if (abortController.signal.aborted) {
        this._removeRunningTask(id)
        // Resolve rather than reject: several callers fire tasks without
        // handling rejections, and a payload-less result is ignored by
        // getTaskResults, so a cancelled file counts as neither success nor
        // failure.
        return Promise.resolve({ cancelled: true })
      }

      const promise = task.func(...task.params, abortController.signal)
      registeredTask.promise = promise

      return promise.finally(() => this._removeRunningTask(id))
    }

    return this._queue.add(job)
  }

  cancelTasks: () => void = () => {
    this._tasks.forEach(task => {
      if (task.abortController) task.abortController.abort()
    })
    this._tasks = []
    this._queue.clear()
  }
}

class UploadTaskRunner {
  static #instance: TaskRunner | null

  constructor() {}

  static getInstance(): TaskRunner | null {
    if (!UploadTaskRunner.#instance) {
      UploadTaskRunner.#instance =
        typeof window !== 'undefined' ? new TaskRunner() : null
    }
    return UploadTaskRunner.#instance
  }
}

/**
 * Singleton instance of the upload task runner.
 *
 * Provides a centralized task runner for managing upload operations,
 * including queuing, processing, and monitoring upload tasks.
 *
 * @example
 * ```typescript
 * // Add upload task to the queue
 * taskRunner.addTask({
 *   file: myFile,
 *   onProgress: (progress) => console.log(`Upload ${progress}% complete`),
 *   onComplete: () => console.log('Upload finished')
 * })
 *
 * // Check task runner status
 * const isRunning = taskRunner.isRunning()
 * const queueLength = taskRunner.getQueueLength()
 * ```
 */
export const taskRunner = UploadTaskRunner.getInstance()

type UploadFileToS3Params = {
  file: File
  uniqueId?: string | null | undefined
  events: UploadEvents
  policyUrl?: string | null | undefined
  skipDuplicatesCheck?: boolean
  abortSignal?: AbortSignal
  csrfToken?: string
}

type UploadFileToS3Result = {
  needsFinalizeUpload: boolean
  info: TempTrack
  uploadPolicy?: any
  uploadError?: Error
  duplicate?: Track
}

/**
 * Custom error class for handling duplicate track uploads.
 *
 * Thrown when attempting to upload a file that already exists in the system.
 * Contains reference to the existing track for handling duplicate scenarios.
 *
 * @example
 * ```typescript
 * try {
 *   await uploadFile(file)
 * } catch (error) {
 *   if (error instanceof DuplicateError) {
 *     console.log('Duplicate track:', error.track)
 *     // Handle duplicate scenario
 *   }
 * }
 * ```
 */
export class DuplicateError extends Error {
  track: Track

  constructor(track: Track) {
    super()
    this.message = 'Duplicate found'
    this.track = track
  }
}

/**
 * Uploads a file to S3 with duplicate checking and progress tracking.
 *
 * Handles the complete file upload process including duplicate detection,
 * S3 upload, and progress callbacks. Returns information about whether
 * finalization is needed.
 *
 * @param params - Upload parameters object
 * @param params.file - The file to upload
 * @param params.uniqueId - Unique identifier for the upload
 * @param params.events - Event handlers for upload lifecycle
 * @param params.policyUrl - S3 policy URL for upload
 * @param params.skipDuplicatesCheck - Whether to skip duplicate checking
 * @param params.abortSignal - Signal for cancelling the upload
 * @returns Promise resolving to upload result with finalization info
 *
 * @example
 * ```typescript
 * const result = await uploadFileToS3({
 *   file: fileObject,
 *   uniqueId: 'unique-123',
 *   events: {
 *     onUploadProgress: (tempTrack) => console.log('Progress:', tempTrack),
 *     onUploadDone: (track) => console.log('Upload complete:', track)
 *   },
 *   policyUrl: 'https://s3-policy-url'
 * })
 *
 * if (result.needsFinalizeUpload) {
 *   await finalizeUpload(result.uploadPolicy)
 * }
 * ```
 */
export function uploadFileToS3({
  file,
  uniqueId,
  events,
  policyUrl,
  skipDuplicatesCheck,
  abortSignal,
}: UploadFileToS3Params): Promise<UploadFileToS3Result> {
  if (abortSignal?.aborted) {
    return Promise.reject(new Error('Upload cancelled'))
  }

  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    onInfoParse = (_info: TempTrack) => {},
    onDuplicateFound = (_track: any, _info: TempTrack) => {},
    onUploadProgress = _track => {},
    onUploadError = (_info, _err) => {},
    /* eslint-enable @typescript-eslint/no-unused-vars */
  } = events

  const activateMultipartUpload = file.size / (1024 * 1024) > 10

  let infoPromiseValue: TempTrack
  let uploadPolicyValue

  return new Promise(resolve => {
    const handleUploadError = (e: Error) => {
      onUploadError(infoPromiseValue, e)
      return resolve({
        needsFinalizeUpload: false,
        info: infoPromiseValue,
        uploadError: e,
      })
    }

    abortSignal?.addEventListener('abort', () => {
      return handleUploadError(new Error('Upload cancelled'))
    })

    getID3Tags(file, uniqueId)
      .then(info => {
        infoPromiseValue = info as TempTrack
        onInfoParse(info as TempTrack)
        // if policyUrl is present we shouldn't try to deduplicate files
        if (isAudioFile(file) && !policyUrl && !skipDuplicatesCheck) {
          return retryPromise(
            () => api.searchForDuplicates(info, abortSignal),
            abortSignal,
          )
        }
        return Promise.resolve({ results: [] })
      })
      .then((duplicatesSearchResult: { results: Track[] }) => {
        if (duplicatesSearchResult.results.length) {
          // track already exists on system
          const track = duplicatesSearchResult.results[0]
          if (track) {
            onDuplicateFound(track, infoPromiseValue)
            return Promise.reject(new DuplicateError(track))
          }
        }
        return Promise.resolve()
      })
      .then(() => {
        // presense of policyUrl controls whether or not we should pass custom urls
        // instead of using default ones, needed for receive pages where
        // user can be unknown to system
        return retryPromise(() => {
          const credentials = activateMultipartUpload
            ? 'TEMPORARY_CREDENTIALS'
            : 'PRESIGNED_POST'
          return api.getUploadPolicy(policyUrl, credentials, abortSignal)
        }, abortSignal)
      })
      .then(uploadPolicy => {
        uploadPolicyValue = uploadPolicy
        // evaporate can retry upload itself, it doesn't need to be wrapped into retryCall
        if (activateMultipartUpload) {
          return import('@disco/api/s3').then(({ uploadToS3Evaporate }) =>
            uploadToS3Evaporate(
              uploadPolicy,
              file,
              infoPromiseValue,
              onUploadProgress,
              onUploadError,
              abortSignal,
            ),
          )
        }

        return retryPromise(() => {
          return api.uploadToS3(
            uploadPolicy,
            file,
            infoPromiseValue,
            onUploadProgress,
            onUploadError,
            abortSignal,
          )
        }, abortSignal)
      })
      .then(() => {
        return resolve({
          needsFinalizeUpload: true,
          info: infoPromiseValue,
          uploadPolicy: uploadPolicyValue,
        })
      })
      .catch(e => {
        if (e instanceof DuplicateError) {
          // DuplicateError is used for early bailout from promise chain, don't throw upload error
          return resolve({
            needsFinalizeUpload: false,
            info: infoPromiseValue,
            duplicate: e.track,
          })
        }

        return handleUploadError(e)
      })
  })
}

type FinalizeUploadParams = {
  filename: string
  uploadPolicy: any
  policyUrl: string | null | undefined
  transcode: boolean
  filesize: number
  abortSignal?: AbortSignal
  csrfToken?: string
}

/**
 * Finalizes an S3 upload by notifying the server and starting processing.
 *
 * Completes the upload process by informing the server that the file has been
 * successfully uploaded to S3 and triggering any necessary post-processing.
 *
 * @param params - Finalization parameters object
 * @param params.filename - Name of the uploaded file
 * @param params.uploadPolicy - S3 upload policy used for the upload
 * @param params.policyUrl - S3 policy URL
 * @param params.transcode - Whether to transcode the file
 * @param params.filesize - Size of the uploaded file in bytes
 * @param params.abortSignal - Signal for cancelling the finalization
 * @returns Promise resolving to the finalized track object
 *
 * @example
 * ```typescript
 * const track = await finalizeUpload({
 *   filename: 'song.mp3',
 *   uploadPolicy: s3Policy,
 *   policyUrl: 'https://policy-url',
 *   transcode: true,
 *   filesize: 1024000
 * })
 * console.log('Track finalized:', track)
 * ```
 */
export function finalizeUpload({
  filename,
  uploadPolicy,
  policyUrl,
  transcode,
  filesize,
  abortSignal,
}: FinalizeUploadParams): Promise<Track> {
  // policy format can differ based on what kind of credentials was used
  const uploadKey = uploadPolicy.fields
    ? uploadPolicy.fields['key']
    : uploadPolicy['Key']
  const respUrl = policyUrl ? uploadPolicy.on_complete : null
  return retryPromise(() => {
    return api.getUploadResult(
      uploadKey,
      filename,
      respUrl,
      transcode,
      filesize,
      abortSignal,
    )
  }, abortSignal)
}

type UploadOptions = {
  skipDuplicatesCheck?: boolean
  skipFinalization?: boolean
}

type UploadEvents = {
  onInfoParse?: (info: TempTrack) => any
  onDuplicateFound?: (track: Track, tempTrack: TempTrack) => any
  onUploadProgress?: (tempTrack: TempTrack) => any
  onUploadError?: (tempTrack: TempTrack, error?: Error) => any
  onUploadDone?: (track: Track, tempTrack: TempTrack) => any
}

/**
 * Uploads a single file with progress tracking.
 *
 * Handles the complete upload process for a single file, including
 * S3 upload, progress tracking, and finalization steps.
 *
 * @param options - Upload configuration options
 * @param options.file - The file to upload
 * @param options.onProgress - Progress callback function
 * @param options.onComplete - Completion callback function
 * @param options.onError - Error callback function
 * @returns Promise resolving to upload result
 *
 * @example
 * ```typescript
 * const file = new File(['content'], 'song.mp3', { type: 'audio/mpeg' })
 *
 * await uploadFile({
 *   file,
 *   onProgress: (progress) => console.log(`${progress}% uploaded`),
 *   onComplete: (result) => console.log('Upload complete:', result),
 *   onError: (error) => console.error('Upload failed:', error)
 * })
 * ```
 */
export async function uploadFile(
  file: File,
  csrfToken: string | null | undefined,
  uniqueId: string | null | undefined,
  events: UploadEvents,
  policyUrl?: string | null | undefined,
  { skipDuplicatesCheck, skipFinalization }: UploadOptions = {},
  // currently not all upload scenarios require cancellation
  abortSignal?: AbortSignal,
): Promise<any> {
  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    onUploadError = (_info: TempTrack, _err) => {},
    onUploadDone = (_track: Track, _tempTrack: TempTrack) => {},
    /* eslint-enable @typescript-eslint/no-unused-vars */
  } = events

  // A cancelled upload is not a failed one: callers use this to keep it out of
  // the "files failed to upload" notification.
  const wasCancelled = () => abortSignal?.aborted === true

  return new Promise((resolve, reject: (error?: any) => void) => {
    // Step 1
    const uploadResult = uploadFileToS3({
      file,
      csrfToken: csrfToken ?? undefined,
      uniqueId: uniqueId ?? undefined,
      events,
      policyUrl: policyUrl ?? undefined,
      skipDuplicatesCheck,
      abortSignal,
    })
      .then(result => {
        const {
          needsFinalizeUpload,
          info,
          uploadPolicy,
          uploadError,
          duplicate,
        } = result

        if (abortSignal?.aborted) {
          const err = new Error('Upload cancelled')
          onUploadError(info, err)
          return resolve({ info, uploadError: err, cancelled: true })
        }

        // Step 2 (optional)
        if (needsFinalizeUpload && !skipFinalization) {
          return finalizeUpload({
            filename: file.name,
            uploadPolicy,
            csrfToken: csrfToken ?? undefined,
            policyUrl: policyUrl ?? undefined,
            transcode: false,
            filesize: file.size,
            abortSignal,
            // don't await server processing
          })
            .then(result => {
              if (abortSignal?.aborted) throw new Error('Upload cancelled')

              onUploadDone(result, info)
              return resolve({ uploadPolicy, result, info })
            })
            .catch(err => {
              onUploadError(info, err)
              return resolve({
                info,
                uploadError: err,
                cancelled: wasCancelled(),
              })
            })
        }

        return resolve({
          info,
          uploadPolicy,
          uploadError,
          duplicate,
          cancelled: wasCancelled(),
        })
      })
      .catch(err => {
        // uploadToS3 supposed to never throw even in case of errors
        captureError(err, {
          description: 'Unexpected file upload promise rejection',
          extra: { level: 'info' },
        })
        return reject(err)
      })

    return uploadResult
  })
}

/**
 * Gets the appropriate link for bulk upload functionality.
 *
 * Returns a link to launch the DISCO Uploader application if it's installed
 * on Windows or Mac OS, otherwise returns a link to the downloads page.
 *
 * @returns Object with href and target properties for the bulk upload link
 *
 * @example
 * ```typescript
 * const link = getBulkUploadDownloadLink()
 * // On Windows/Mac with uploader installed:
 * // { href: 'discoup://launch', target: '_self' }
 *
 * // On other systems or without uploader:
 * // { href: 'https://disco.ac/downloads', target: '_blank' }
 * ```
 */
export const getBulkUploadDownloadLink = (): any | null | undefined => {
  const browser = detect()
  const os = get(browser, 'os', '')
  if (os && (os.includes('Windows') || os === 'Mac OS')) {
    if (Storage.getSync('oneClickUploaderLaunch'))
      return { href: 'discoup://launch', target: '_self' }
  }
  return { href: `${DISCO_SITE}/downloads`, target: '_blank' }
}

// Check if files are within size limits, if not, show a message and return the valid files
const checkFileSizes = (
  files: TempItem[],
  maxFileSize: number,
  minFileSize: number = 1,
): {
  validFiles: TempItem[]
  filesTooLarge: TempItem[]
  filesTooSmall: TempItem[]
} =>
  files.reduce(
    (acc, file) => {
      const size = file.size
      if (size > maxFileSize) {
        acc.filesTooLarge.push(file)
      } else if (size < minFileSize) {
        acc.filesTooSmall.push(file)
      } else {
        acc.validFiles.push(file)
      }
      return acc
    },
    {
      validFiles: [] as TempItem[],
      filesTooLarge: [] as TempItem[],
      filesTooSmall: [] as TempItem[],
    },
  )

/**
 * Verifies and filters files for upload, showing notifications for invalid files.
 *
 * Validates file sizes, counts, and other criteria before upload. Shows
 * appropriate notifications for files that don't meet requirements.
 *
 * @param files - Array of temporary file items to verify
 * @param onNotification - Callback to show notifications for invalid files
 * @returns Array of valid files that can be uploaded
 *
 * @example
 * ```typescript
 * const validFiles = verifyFiles(tempItems, (notification) => {
 *   console.log('File validation error:', notification.message)
 * })
 * // Only valid files will be returned for upload
 * ```
 */
export const verifyFiles = (
  files: TempItem[],
  onNotification: (notification: UINotification) => void,
): TempItem[] => {
  // Split files into valid and invalid
  const { validFiles, filesTooLarge, filesTooSmall } = checkFileSizes(
    files,
    MAX_UPLOAD_SIZE,
  )

  if (validFiles.length > MAX_UPLOAD_FILES_ERROR_LIMIT) {
    onNotification({
      title: 'Error: Too many files',
      message: `Sorry! The maximum number of files you can upload at once is ${MAX_UPLOAD_FILES_ERROR_LIMIT}. Check out our Bulk Uploader for larger uploads.`,
      level: 'error',
      dismissible: 'button',
      action: {
        label: 'Learn more',
        callback: () => {
          window.open(
            'https://support.disco.ac/home/using-the-disco-bulk-uploader-tool',
            '_blank',
          )
        },
      },
    })

    // Do not return any files to upload!
    return []
  }

  if (validFiles.length > MAX_UPLOAD_FILES_WARNING_LIMIT) {
    onNotification({
      title: 'Heads up: This could take a while',
      message:
        'You’ve added a lot of files. This may take some time to upload. Check out our Bulk Uploader for larger uploads.',
      level: 'warning',
      dismissible: 'button',
      action: {
        label: 'Learn more',
        callback: () => {
          window.open(
            'https://support.disco.ac/home/using-the-disco-bulk-uploader-tool',
            '_blank',
          )
        },
      },
    })
  }

  // If files are too large, show a message
  if (filesTooLarge.length) {
    const moreThanOne = filesTooLarge.length > 1
    const filesReadable = filesTooLarge.map(file => {
      const size = file.size
      return `${file.title} (${formatFileSize(size, 2)})`
    })

    const title = moreThanOne
      ? 'Warning: Files too large'
      : 'Warning: File too large'

    const message = `${
      moreThanOne
        ? 'Sorry! The following files are too large to upload: '
        : 'Sorry! This file is too large to upload: '
    }${filesReadable.join(', ')}. The maximum file size is ${formatFileSize(
      MAX_UPLOAD_SIZE,
      0,
    )}.`

    onNotification({
      title,
      message,
      level: 'warning',
      dismissible: 'button',
    })
  }

  // If files are too small, show a message
  if (filesTooSmall.length) {
    const moreThanOne = filesTooSmall.length > 1
    const filesReadable = filesTooSmall.map(file => file.title)

    const title = moreThanOne ? 'Warning: Empty files' : 'Warning: Empty file'

    const message = `${
      moreThanOne
        ? "Sorry! Some files you've added are empty and can't be uploaded. Please check the following files: "
        : "Sorry! The file you've added is empty and can't be uploaded. Please check the following file: "
    }${filesReadable.join(', ')}.`

    onNotification({
      title,
      message,
      level: 'warning',
      dismissible: 'button',
    })
  }

  return validFiles
}

/**
 * Prepares File objects for upload by converting them to temporary items.
 *
 * Converts native File objects into TempItem objects with additional metadata
 * and unique identifiers needed for the upload process.
 *
 * @param files - Array of File objects to prepare
 * @returns Array of temporary items ready for upload processing
 *
 * @example
 * ```typescript
 * const fileInput = document.getElementById('fileInput') as HTMLInputElement
 * const files = Array.from(fileInput.files || [])
 * const tempItems = prepareTempItems(files)
 * // tempItems now have unique IDs and metadata for upload
 * ```
 */
export const prepareTempItems = (files: File[]): TempItem[] => {
  return files.map(file => ({
    tempId: cuid(),
    filename: file.name,
    title: file.name,
    album: undefined,
    artist: undefined,
    ext: getExtension(file.name),
    progress: 0,
    type: TRACK,
    groups: [],

    // Reference to the original file object to send to the server
    file: file,

    // Stored here so we can generically verify the file size
    size: file.size,
  }))
}
